import React, { useCallback, useEffect, useMemo } from "react";
import {
  Message,
  MessageAction,
  ResearchId,
} from "../../../adapters/insights-agent/state/AppState.ts";
import {
  useMessagesState,
  useResearchState,
  useResearchTypes,
} from "../../../adapters/insights-agent/state/hooks/stateHooks.ts";
import { useInsightsAgentService } from "../../../adapters/insights-agent/state/hooks/useInsightsAgentService.tsx";
import { useFeatureFlagValue } from "../../../config/FeatureFlagManager/hooks.ts";
import { LegacyChatMessages } from "../../../feature-components/LegacyChatMessages/LegacyChatMessages.tsx";

const useFlags = (): {
  enableRelatedQuestions: boolean;
  enableAppliedFilters: boolean;
  enableNumberedCitations: boolean;
  enableSplitCitations: boolean;
} => {
  const enableRelatedQuestions = useFeatureFlagValue("enableRelatedQuestions");
  const enableAppliedFilters = useFeatureFlagValue("enableAppliedFilters");
  const enableNumberedCitations = useFeatureFlagValue(
    "enableNumberedCitations",
  );
  const enableSplitCitations = useFeatureFlagValue("enableSplitCitations");
  return useMemo(
    () => ({
      enableRelatedQuestions,
      enableAppliedFilters,
      enableNumberedCitations,
      enableSplitCitations,
    }),
    [
      enableRelatedQuestions,
      enableAppliedFilters,
      enableNumberedCitations,
      enableSplitCitations,
    ],
  );
};
export const ConnectedChatMessages: React.FC<{ researchId: ResearchId }> = (
  props,
) => {
  const research = useResearchState(props.researchId);
  const researchStatus = research?.status;
  const messages = useMessagesState(props.researchId);
  const researchTypes = useResearchTypes();
  const appService = useInsightsAgentService();

  const featureFlags = useFlags();
  useEffect(() => {
    appService.refreshResearchItem({ id: props.researchId }).then();
    appService.refreshResearchTypes().then();
    if (researchStatus === "completed") {
      appService.refreshMessages({ researchId: props.researchId }).then();
    }
  }, [appService, props.researchId, researchStatus]);
  const sortedMessages: Message[] = useMemo(
    () =>
      [...messages].sort(
        (a, b) => Date.parse(a.create_time) - Date.parse(b.create_time),
      ),
    [messages],
  );
  const prompt = useMemo(
    () =>
      !research
        ? undefined
        : {
            brief: research.brief,
            questions: research.questions,
          },
    [research],
  );
  const researchType = research?.research_type;
  const researchTypeDisplayName = useMemo(() => {
    if (!researchType || !researchTypes.items.length) {
      return researchType;
    }
    const matchingType = researchTypes.items.find(
      (type) => type.name === researchType,
    );
    return matchingType?.display_name || researchType;
  }, [researchType, researchTypes.items]);

  const onActionClick = useCallback(
    (action: MessageAction) => {
      if (action.type === "submit-related-question") {
        appService
          .sendMessage({
            researchId: props.researchId,
            message: action.parameters.query,
          })
          .then();
      }
    },
    [appService, props.researchId],
  );
  return (
    <LegacyChatMessages
      researchStatus={researchStatus}
      messages={sortedMessages}
      prompt={prompt}
      researchType={researchTypeDisplayName}
      onActionClick={onActionClick}
      featureFlags={featureFlags}
    />
  );
};
